import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SERVICES, VALUES, PROCESS, IMAGES, HOME_SERVICE_IMAGES } from "../data/site";
import { Reveal, FadeIn } from "../components/Reveal";
import { SectionLabel, ContactCTA } from "../components/Shared";
import { Intro } from "../components/Intro";

export default function Home() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 90]);

  useEffect(() => {
    document.title = "Orbis Forum Català — Consultoria internacional privada";
  }, []);

  return (
    <div>
      <Intro />
      {/* HERO */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
        <motion.div style={{ y: heroY }} className="absolute inset-0 z-0">
          <img src={IMAGES.skyline} alt="Centre financer internacional" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ofc-black via-ofc-black/85 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-ofc-black via-ofc-black/30 to-ofc-black/40" />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28 pb-16">
          <FadeIn delay={0.1}>
            <SectionLabel>Firma privada · Orientació internacional</SectionLabel>
          </FadeIn>
          <FadeIn delay={0.25}>
            <h1 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.04] mt-6 max-w-4xl text-balance">
              Projectem els interessos catalans arreu del món
            </h1>
          </FadeIn>
          <FadeIn delay={0.4}>
            <p className="mt-6 text-base md:text-lg text-white/70 font-light leading-relaxed max-w-xl">
              Consultoria internacional privada al servei exclusiu dels ciutadans i les empreses
              de Catalunya. Assessorament, documentació i acompanyament amb la màxima discreció.
            </p>
          </FadeIn>
          <FadeIn delay={0.55}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contacte"
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center gap-3 bg-ofc-gold text-ofc-black px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-ofc-goldhover transition-colors duration-300 group"
              >
                Sol·licitar consulta
                <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/serveis"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-3 border border-white/20 text-white px-8 py-4 text-xs uppercase tracking-[0.2em] hover:border-ofc-gold hover:text-ofc-gold transition-colors duration-300"
              >
                Els nostres serveis
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PRESENTACIÓ */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <Reveal className="lg:col-span-4">
              <SectionLabel>Qui som</SectionLabel>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-8">
              <p className="font-serif-display font-light text-2xl md:text-4xl leading-snug tracking-tight text-white/90 text-balance">
                Orbis Forum Català és una firma privada creada per ajudar ciutadans i empreses
                catalanes a desenvolupar els seus projectes internacionals amb rigor, reserva
                i una visió estratègica global.
              </p>
              <p className="mt-8 text-white/55 font-light leading-relaxed max-w-2xl">
                No som una administració pública ni un organisme oficial. Som un interlocutor
                privat i independent que acompanya els vostres interessos més enllà de les
                fronteres, amb la confidencialitat pròpia de la banca privada.
              </p>
              <Link
                to="/qui-som"
                className="mt-8 inline-flex items-center gap-2 text-sm text-ofc-gold hover:text-ofc-goldhover transition-colors group"
              >
                Conèixer la firma
                <ArrowRight size={15} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVEIS */}
      <section className="border-t border-white/5 bg-ofc-navy/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <SectionLabel>Serveis</SectionLabel>
              <h2 className="font-serif-display font-light text-3xl md:text-5xl tracking-tight mt-6">
                Àrees d'especialització
              </h2>
            </div>
            <Link to="/serveis" className="text-sm text-white/60 hover:text-ofc-gold transition-colors whitespace-nowrap">
              Veure tots els serveis →
            </Link>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                <Link
                  to={`/serveis/${s.slug}`}
                  data-testid={`home-service-${s.slug}`}
                  className="group relative flex flex-col h-full bg-ofc-black hover:bg-ofc-navy transition-colors duration-500"
                >
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img
                      src={HOME_SERVICE_IMAGES[s.slug]}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ofc-black/70 via-ofc-black/10 to-transparent" />
                  </div>
                  <div className="flex flex-col justify-between flex-1 p-8 md:p-10">
                    <div>
                      <span className="text-xs text-ofc-gold/70 tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                      <h3 className="font-serif-display text-2xl mt-4 leading-tight group-hover:text-ofc-gold transition-colors">
                        {s.title}
                      </h3>
                      <p className="mt-4 text-sm text-white/50 font-light leading-relaxed">{s.short}</p>
                    </div>
                    <ArrowUpRight
                      size={20}
                      strokeWidth={1.5}
                      className="mt-8 text-white/30 group-hover:text-ofc-gold group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300"
                    />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VALORS */}
      <section className="border-t border-white/5 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <SectionLabel>Valors</SectionLabel>
            <h2 className="font-serif-display font-light text-3xl md:text-5xl tracking-tight mt-6 max-w-2xl">
              Els principis que guien cada relació
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12 mt-16">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-px w-12 bg-ofc-gold mb-6" />
                <h3 className="font-serif-display text-2xl">{v.title}</h3>
                <p className="mt-4 text-sm text-white/50 font-light leading-relaxed">{v.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCÉS */}
      <section className="border-t border-white/5 bg-ofc-navy/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <SectionLabel>Procés de treball</SectionLabel>
            <h2 className="font-serif-display font-light text-3xl md:text-5xl tracking-tight mt-6 max-w-2xl">
              Un mètode ordenat, discret i orientat a resultats
            </h2>
          </Reveal>
          <div className="mt-16 divide-y divide-white/5 border-y border-white/5">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.06}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-8 md:py-10 group">
                  <span className="md:col-span-2 font-serif-display text-3xl text-ofc-gold/70">{p.step}</span>
                  <h3 className="md:col-span-3 font-serif-display text-2xl">{p.title}</h3>
                  <p className="md:col-span-7 text-white/50 font-light leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ContactCTA />
    </div>
  );
}
